import type { ReactNode } from "react";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useFormatters } from "@/lib/hooks/useFormatters";
import { useDetailFieldSectionContext } from "../context/DetailFieldSectionContext";
import { formatDetailFieldValue } from "../utils/formatDetailFieldValue";
import { getDetailFieldLabel } from "../utils/getDetailFieldLabel";
import { getDetailFieldValue } from "../utils/getDetailFieldValue";

export interface UseDetailFieldOptions {
  field: string;
  value?: ReactNode;
  label?: string;
  labelKey?: string;
}

export interface UseDetailFieldResult {
  value: ReactNode;
  label: string;
}

export function useDetailField({
  field,
  value: valueProp,
  label: labelProp,
  labelKey,
}: UseDetailFieldOptions): UseDetailFieldResult {
  const { t } = useTranslation();
  const { data, schema, module } = useDetailFieldSectionContext();
  const formatters = useFormatters();

  return useMemo(() => {
    const hasCustomValue = valueProp !== undefined;
    const rawValue = getDetailFieldValue(data, field);
    const value = hasCustomValue
      ? valueProp
      : formatDetailFieldValue(schema, field, rawValue, formatters);
    const label = labelProp ?? getDetailFieldLabel(labelKey, module, field, t);
    return { value, label };
  }, [
    field,
    valueProp,
    labelProp,
    labelKey,
    data,
    schema,
    module,
    t,
    formatters,
  ]);
}
